import { z } from 'zod'

// No relative imports here: the landing site's tests load this file straight into Node to check its package cards.

// Packages sold with a web + adminweb pair, and the entitlements each one grants (issue #69).
// A site stores its package plus optional per-site overrides; nothing stored means the current
// production behaviour: Package 3 (`advanced`).

export const PACKAGE_IDS = ['starter', 'business', 'advanced', 'store'] as const
export const PackageIdSchema = z.enum(PACKAGE_IDS)
export type PackageId = z.infer<typeof PackageIdSchema>

/** On/off capabilities. */
export const FEATURE_FLAGS = [
  'cms', // customer sign-in to the CMS at all
  'blog', // journal / news articles
  'lead_capture', // configurable lead form with notifications
  'booking', // booking / calendar embed block
  'rename_types', // rename the built-in catalogue types
  'custom_types', // add and delete catalogue types
  'rbac', // custom roles and permissions
  'multi_location', // locations list and location pages
  'custom_domain', // own domain instead of a platform subdomain
  'store', // online store
  'mailbox', // inbox for mail sent to the site's own domain (Cloudflare Email Routing → API Worker)
  'ai_writing', // AI writing assistant in the editors (the site's own Anthropic API key)
  'seo', // SEO fields of pages, articles and catalogue entries, and the site-wide SEO settings
  'tracking', // Google Analytics, Meta Pixel and TikTok tracking settings, and their scripts on the public site
] as const
export const FeatureFlagSchema = z.enum(FEATURE_FLAGS)
export type FeatureFlag = z.infer<typeof FeatureFlagSchema>

/**
 * Numeric caps. Every package has a concrete number for each: nothing is sold as unlimited, because pages, content
 * and above all media storage cost real money. A stored `null` override (from before caps) reads as the package cap.
 *
 * - pages: static block pages (Home, About…); articles and catalogue entries do not count
 * - items: catalogue entries — products, industries, materials/finishes and items of the site's own sections
 * - articles: journal/news articles plus items of the site's own article-style sections
 * - storage: media library size in MB
 * - domains: the client's own domains connected to the site (each one is a zone in our Cloudflare account)
 */
export const LIMIT_KEYS = ['pages', 'users', 'languages', 'items', 'articles', 'storage', 'domains'] as const
export const LimitKeySchema = z.enum(LIMIT_KEYS)
export type LimitKey = z.infer<typeof LimitKeySchema>

/**
 * Limits the console counts and enforces itself, not the site: domains are kept by the console (it holds the Cloudflare
 * token), so the site does not count them. Its CMS shows them on the Domains screen, as the console reports them.
 */
export const CONSOLE_LIMIT_KEYS = ['domains'] as const satisfies readonly LimitKey[]
export type ConsoleLimitKey = (typeof CONSOLE_LIMIT_KEYS)[number]
/** Limits the site counts (its usage report) and its CMS shows. */
export type SiteLimitKey = Exclude<LimitKey, ConsoleLimitKey>
export const SITE_LIMIT_KEYS = LIMIT_KEYS.filter((k): k is SiteLimitKey => !(CONSOLE_LIMIT_KEYS as readonly string[]).includes(k))

/**
 * How much of the CMS a package opens. `full`: every area, as far as the user's role allows. `content`: only the
 * texts and pictures of the pages and catalogue (areas in CONTENT_SCOPE_AREAS); site settings, navigation, users,
 * articles, enquiries and the rest stay closed to the site's own account, whatever its role. Console staff are not
 * limited by the scope.
 */
export const PLAN_SCOPES = ['full', 'content'] as const
export type PlanScope = (typeof PLAN_SCOPES)[number]
export const CONTENT_SCOPE_AREAS: readonly string[] = ['pages', 'catalogue', 'media'] // permission areas (permissions.ts)

type Preset = { flags: readonly FeatureFlag[]; limits: Record<LimitKey, number>; scope: PlanScope }

const BUSINESS: Preset = {
  flags: ['cms', 'blog', 'lead_capture', 'booking', 'rename_types', 'custom_domain', 'seo', 'tracking'],
  limits: { pages: 10, users: 5, languages: 2, items: 100, articles: 50, storage: 1024, domains: 1 },
  scope: 'full',
}
const ADVANCED: Preset = {
  flags: [...BUSINESS.flags, 'custom_types', 'rbac', 'multi_location', 'mailbox', 'ai_writing'],
  limits: { pages: 25, users: 20, languages: 5, items: 1000, articles: 300, storage: 5120, domains: 3 },
  scope: 'full',
}

/** The single source of truth for what each package includes: the API, CMS, console and the landing site read it. */
export const PACKAGE_PRESETS: Record<PackageId, Preset> = {
  // One CMS account (the owner) that edits the texts and pictures of its pages and catalogue.
  starter: { flags: ['cms'], limits: { pages: 3, users: 1, languages: 1, items: 20, articles: 0, storage: 250, domains: 0 }, scope: 'content' },
  business: BUSINESS,
  advanced: ADVANCED,
  store: { flags: [...ADVANCED.flags, 'store'], limits: { pages: 30, users: 20, languages: 5, items: 1000, articles: 300, storage: 5120, domains: 3 }, scope: 'full' },
}

export const PACKAGE_NAMES: Record<PackageId, string> = {
  starter: 'Starter',
  business: 'Business',
  advanced: 'Advanced',
  store: 'Store',
}

/**
 * What is stored for a site (site document `entitlements`): its package and any per-site exceptions. Overrides go
 * under `overrides`, e.g. a Business site allowed three content languages:
 * `{"package":"business","overrides":{"limits":{"languages":3}}}` (a top-level `limits` key is ignored).
 */
export const EntitlementsSchema = z.object({
  package: PackageIdSchema,
  /** Suspended by the platform: the CMS refuses customer sign-ins and edits (staff can still sign in through the console). */
  suspended: z.boolean().default(false),
  overrides: z
    .object({
      flags: z.partialRecord(FeatureFlagSchema, z.boolean()).default({}),
      limits: z.partialRecord(LimitKeySchema, z.number().int().min(0).nullable()).default({}),
    })
    .default({ flags: {}, limits: {} }),
})
export type Entitlements = z.infer<typeof EntitlementsSchema>

/** Package and overrides applied: the values the CMS actually enforces. */
export type ResolvedEntitlements = {
  package: PackageId
  suspended: boolean
  scope: PlanScope
  flags: Record<FeatureFlag, boolean>
  limits: Record<LimitKey, number | null>
}

export const DEFAULT_PACKAGE: PackageId = 'advanced'

export function resolveEntitlements(stored: unknown): ResolvedEntitlements {
  const parsed = EntitlementsSchema.safeParse(stored)
  const { package: id, overrides, suspended } = parsed.success ? parsed.data : { package: DEFAULT_PACKAGE, overrides: { flags: {}, limits: {} }, suspended: false }
  const preset = PACKAGE_PRESETS[id]
  const flags = Object.fromEntries(FEATURE_FLAGS.map((f) => [f, overrides.flags[f] ?? preset.flags.includes(f)])) as Record<FeatureFlag, boolean>
  // Every plan is capped: a legacy `null` ("unlimited") override falls back to the package's number.
  const limits = Object.fromEntries(LIMIT_KEYS.map((k) => [k, overrides.limits[k] ?? preset.limits[k]])) as Record<LimitKey, number | null>
  return { package: id, suspended, scope: preset.scope, flags, limits }
}

/** Whether the package opens permission area `area` of the CMS to the site's own users (console staff are not limited by it). */
export const planAllowsArea = (e: Pick<ResolvedEntitlements, 'scope'>, area: string): boolean => e.scope !== 'content' || CONTENT_SCOPE_AREAS.includes(area)

export const can = (e: ResolvedEntitlements, flag: FeatureFlag): boolean => e.flags[flag]

/** Whether one more of `key` fits, given how many exist now. */
export const canAdd = (e: ResolvedEntitlements, key: LimitKey, current: number): boolean => {
  const limit = e.limits[key]
  return limit === null || current < limit
}

export type LimitState = { limit: number | null; used: number; remaining: number | null; reached: boolean }

export function limitState(e: ResolvedEntitlements, key: LimitKey, used: number): LimitState {
  const limit = e.limits[key]
  return { limit, used, remaining: limit === null ? null : Math.max(0, limit - used), reached: limit !== null && used >= limit }
}

/** What GET /api/admin/site/entitlements returns: the plan and the current usage against it. */
export const SitePlanSchema = z.object({
  package: PackageIdSchema,
  suspended: z.boolean().default(false),
  /** Absent from a site on an older version: those had no content-only package. */
  scope: z.enum(PLAN_SCOPES).default('full'),
  // Partial: a site on an older version reports fewer flags; a missing flag reads as "not reported".
  flags: z.partialRecord(FeatureFlagSchema, z.boolean()),
  // Partial: a site on an older version reports fewer keys; a missing key reads as "not reported".
  limits: z.partialRecord(LimitKeySchema, z.number().nullable()),
  usage: z.partialRecord(LimitKeySchema, z.number()),
})
export type SitePlan = z.infer<typeof SitePlanSchema>
